import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { showToast } from "../utils/toast";
import { getAxiosInstance } from "../utils/apiClient";
import { getSocket } from "../utils/websocket";
import { setUser } from "../redux/modules/userSlice";

interface SocketData {
  type: string;
  message: string;
  timestamp: string;
  user: {
    id: string;
    username: string;
    firstName: string;
    lastName: string;
  };
}

const useAgeApproveSocket = () => {
  const dispatch = useDispatch();
  const user = useSelector((state: any) => state.user.currentUser);

  useEffect(() => {
    const socket = getSocket();
    if (!socket) return;

    // Слухач для схвалення акаунта
    const handleAccountApproved = (data: SocketData) => {
      // Показати повідомлення про схвалення
      showToast(data.message, "success");

      // Оновити стан користувача в Redux
      setUser({
        ...user,
        currentUser: {
          ...user.currentUser,
          status: "approved",
          approvedAt: data.timestamp,
        },
      });

      // Оновити локальне сховище
      const currentUser = JSON.parse(localStorage.getItem("user") || "{}");
      if (currentUser.id === data.user.id) {
        const updatedUser = {
          ...currentUser,
          status: "approved",
          approvedAt: data.timestamp,
        };
        localStorage.setItem("user", JSON.stringify(updatedUser));
      }
    };

    // Слухач для схвалення доказів віку
    const handleAgeProofApproved = (data: SocketData) => {
      // Показати повідомлення про схвалення доказів віку
      showToast(data.message, "success");

      // Оновити стан користувача в Redux
      //   dispatch({
      //     type: "user/updateAgeProofStatus",
      //     payload: {
      //       id: data.user.id,
      //       ageProofStatus: "approved",
      //       ageProofApprovedAt: data.timestamp,
      //     },
      //   });

      dispatch(
        setUser({
          ...user,
          ageProofed: true,
          ageProofApprovedAt: data.timestamp,
        })
      );

      // Оновити локальне сховище
      const currentUser = JSON.parse(localStorage.getItem("user") || "{}");
      if (currentUser.id === data.user.id) {
        const updatedUser = {
          ...currentUser,
          ageProofed: true,
          ageProofApprovedAt: data.timestamp,
        };
        localStorage.setItem("user", JSON.stringify(updatedUser));
      }
    };

    // Слухач для відхилення акаунта
    const handleAccountRejected = (data: SocketData) => {
      showToast(data.message, "error");

      setUser({
        ...user,
        currentUser: {
          ...user.currentUser,
          status: "rejected",
          rejectedAt: data.timestamp,
        },
      });

      const currentUser = JSON.parse(localStorage.getItem("user") || "{}");
      if (currentUser.id === data.user.id) {
        const updatedUser = {
          ...currentUser,
          status: "rejected",
          rejectedAt: data.timestamp,
        };
        localStorage.setItem("user", JSON.stringify(updatedUser));
      }
    };

    // Слухач для відхилення доказів віку
    const handleAgeProofRejected = (data: SocketData) => {
      showToast(data.message, "error");

      setUser({
        ...user,
        currentUser: {
          ...user.currentUser,
          ageProofStatus: "rejected",
          ageProofRejectedAt: data.timestamp,
        },
      });

      const currentUser = JSON.parse(localStorage.getItem("user") || "{}");
      if (currentUser.id === data.user.id) {
        const updatedUser = {
          ...currentUser,
          ageProofStatus: "rejected",
          ageProofRejectedAt: data.timestamp,
        };
        localStorage.setItem("user", JSON.stringify(updatedUser));
      }
    };

    // Підключення слухачів подій
    socket.on("accountApproved", handleAccountApproved);
    socket.on("ageProofApproved", handleAgeProofApproved);
    socket.on("accountRejected", handleAccountRejected);
    socket.on("ageProofRejected", handleAgeProofRejected);

    // Очищення слухачів при розмонтуванні
    return () => {
      socket.off("accountApproved", handleAccountApproved);
      socket.off("ageProofApproved", handleAgeProofApproved);
      socket.off("accountRejected", handleAccountRejected);
      socket.off("ageProofRejected", handleAgeProofRejected);
    };
  }, [user, dispatch, setUser]);

  // Функція для відправки запиту на оновлення статусу користувача
  const refreshUserStatus = async (userId: string) => {
    try {
      const response = await getAxiosInstance().get(
        `/api/user/${userId}/status`
      );
      if (response.data.success) {
        const userData = response.data.user;

        // Оновити Redux store
        dispatch({
          type: "user/updateUser",
          payload: userData,
        });

        // Оновити localStorage
        localStorage.setItem("user", JSON.stringify(userData));

        return userData;
      }
    } catch (error) {
      console.error("Error refreshing user status:", error);
      showToast("Error updating user status", "error");
    }
  };

  return {
    refreshUserStatus,
  };
};

export default useAgeApproveSocket;
